import { productAlias } from './ProductAlias'
import { productCatalog } from './ProductCatalog'
import { productFingerprint } from './ProductFingerprint'
import { productMatcher } from './ProductMatcher'
import { productNormalizer } from './ProductNormalizer'
import { productParser } from './ProductParser'
import { productValidator } from './ProductValidator'
import type {
  EnrichableSearchItem,
  EnrichedSearchItem,
  ParsedProductHints,
  ProductMatchGroup,
  ProductModel,
  ProductValidationResult,
} from './ProductTypes'

/**
 * ProductEngine — façade for Product Intelligence (Sprint 2.1).
 * Decoupled from Search Engine; consumes descriptions/results via bridge methods.
 */
export class ProductEngine {
  reset(): void {
    productCatalog.clear()
  }

  parse(description: string): ParsedProductHints {
    return productParser.parse(description)
  }

  normalize(value: string): string {
    return productNormalizer.normalize(value)
  }

  validate(description: string): ProductValidationResult {
    return productValidator.validateHints(productParser.parse(description))
  }

  /**
   * Parse → fingerprint → aliases → catalog upsert.
   */
  recognize(description: string, options: { sku?: string; ean?: string } = {}): ProductModel {
    const hints = productParser.parse(description)
    if (options.sku && !hints.partNumber) {
      hints.partNumber = productNormalizer.normalize(options.sku).replace(/\s+/g, '')
    }

    const existingByAlias = productCatalog.getByAlias(description)
    if (existingByAlias) {
      return productCatalog.upsert({
        ...existingByAlias,
        aliases: productAlias.buildAliases(hints, existingByAlias.fingerprint),
      })
    }

    // Collapse equivalent ads (brand+model) into one catalog identity
    const family = this.findFamilyMatch(hints)
    const fingerprint = family?.fingerprint ?? productFingerprint.build(hints)
    const existingByFp = productCatalog.getByFingerprint(fingerprint)
    const id = existingByFp?.id ?? family?.id ?? crypto.randomUUID()
    const aliases = productAlias.buildAliases(hints, fingerprint)

    const product: ProductModel = {
      id,
      category: hints.category || family?.category || 'Geral',
      brand: hints.brand || family?.brand || '',
      line: hints.line || family?.line || '',
      model: hints.model || family?.model || '',
      partNumber: hints.partNumber || family?.partNumber || '',
      capacity: hints.capacity || family?.capacity || '',
      technology: hints.technology.join(' | ') || family?.technology || '',
      socket: hints.socket || family?.socket,
      generation: hints.generation || family?.generation,
      aliases,
      fingerprint,
      attributes: { ...(family?.attributes ?? {}), ...hints.attributes },
    }

    return productCatalog.upsert(product)
  }

  private findFamilyMatch(hints: ReturnType<typeof productParser.parse>): ProductModel | null {
    if (!hints.brand || !hints.model) return null
    const brand = productNormalizer.normalize(hints.brand)
    const model = productNormalizer.normalize(hints.model)
    for (const product of productCatalog.list()) {
      if (
        productNormalizer.normalize(product.brand) === brand &&
        productNormalizer.normalize(product.model) === model
      ) {
        return product
      }
    }
    return null
  }

  matchMany(descriptions: string[]): ProductMatchGroup[] {
    return productMatcher.group(descriptions, (raw) => this.recognize(raw))
  }

  /**
   * Search Engine integration bridge (additive — does not modify Search module).
   * Pesquisa → (Search results) → Parser → Matcher → Catalog → resultado enriquecido
   */
  enrichSearchResults<T extends EnrichableSearchItem>(items: T[]): EnrichedSearchItem<T>[] {
    return items.map((source) => {
      const product = this.recognize(source.description, {
        sku: source.sku,
        ean: source.ean,
      })
      return {
        source,
        product,
        groupKey: product.fingerprint,
      }
    })
  }

  groupEnriched<T extends EnrichableSearchItem>(
    enriched: EnrichedSearchItem<T>[],
  ): Map<string, EnrichedSearchItem<T>[]> {
    const map = new Map<string, EnrichedSearchItem<T>[]>()
    for (const item of enriched) {
      const list = map.get(item.groupKey) ?? []
      list.push(item)
      map.set(item.groupKey, list)
    }
    return map
  }

  getCatalog() {
    return productCatalog.snapshot()
  }

  getProduct(fingerprintOrAlias: string): ProductModel | null {
    return (
      productCatalog.getByFingerprint(fingerprintOrAlias) ??
      productCatalog.getByAlias(fingerprintOrAlias)
    )
  }
}

export const productEngine = new ProductEngine()
